/**
 * Money is always an integer in minor units plus an ISO-4217 code (§10.4
 * conventions). There is no float anywhere in this codebase, and no `number`
 * representing a major-unit amount.
 */

export interface Money {
  /** Minor units. 500 = $5.00 in USD. */
  amount: number;
  currency: string;
}

export function money(amount: number, currency = 'USD'): Money {
  if (!Number.isInteger(amount)) {
    throw new Error(`money amount must be an integer in minor units, got ${amount}`);
  }
  return { amount, currency };
}

const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'VND', 'CLP', 'ISK']);

export function minorUnitExponent(currency: string): number {
  return ZERO_DECIMAL.has(currency.toUpperCase()) ? 0 : 2;
}

export function formatMoney(m: Money, locale = 'en-US'): string {
  const exp = minorUnitExponent(m.currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: m.currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  }).format(m.amount / 10 ** exp);
}

/**
 * Cost-plus generation pricing (§8.3). Integer arithmetic with round-half-up, so
 * the result is reproducible from the stored fee schedule version.
 */
export function applyMarkup(providerCostMinor: number, markupPercent: number): number {
  const scaled = providerCostMinor * (10000 + Math.round(markupPercent * 100));
  return Math.floor((scaled + 5000) / 10000);
}
